import { useState } from 'react'
import {
  Mail, MessageSquare, Linkedin, Phone, AlertCircle,
  ChevronRight, Clock, User, Zap, TrendingUp, CheckCircle,
  AlertTriangle, Volume2, Send
} from 'lucide-react'

export default function MultiChannelDashboardPage() {
  const [activeTab, setActiveTab] = useState('overview')
  const [escalationFilter, setEscalationFilter] = useState('all')

  // Mock data
  const overview = {
    email: { total: 47, pending: 8, apps: 28, training: 19 },
    discord: { total: 142, unanswered: 12, escalated: 3 },
    linkedin: { total: 24, messages: 16, comments: 8, escalated: 2 },
    calls: { total: 89, pending: 3, today: 12 },
    escalations: { pending: 5, critical: 1, highPriority: 2 }
  }

  const escalations = [
    {
      id: 1,
      type: 'email',
      severity: 'critical',
      source: 'Apps Support',
      reason: 'Angry customer demanding refund',
      time: '2 minutes ago',
      from: 'john.doe@company.com',
      preview: 'I paid for the premium plan and it\'s not working...',
      smsAlert: true,
      emailAlert: true
    },
    {
      id: 2,
      type: 'discord',
      severity: 'high',
      source: 'Student Questions',
      reason: 'Complex technical question about module 5',
      time: '15 minutes ago',
      from: 'StudentXYZ',
      preview: 'How do I implement the advanced encryption method in Python?',
      smsAlert: false,
      emailAlert: true
    },
    {
      id: 3,
      type: 'call',
      severity: 'high',
      source: 'TextNow Support Line',
      reason: 'Customer expressing frustration, needs escalation',
      time: '22 minutes ago',
      from: '+1-412-555-0198',
      preview: 'Call duration: 4:32, Sentiment: Frustrated',
      smsAlert: true,
      emailAlert: false
    },
    {
      id: 4,
      type: 'linkedin',
      severity: 'medium',
      source: 'LinkedIn Page',
      reason: 'Partnership inquiry - needs executive review',
      time: '35 minutes ago',
      from: 'Sarah Marketing Director',
      preview: 'We\'d like to explore a partnership opportunity...',
      smsAlert: false,
      emailAlert: true
    },
    {
      id: 5,
      type: 'email',
      severity: 'medium',
      source: 'Training Center',
      reason: 'Student needs course access verification',
      time: '1 hour ago',
      from: 'student@training.com',
      preview: 'I purchased the course but cannot access it.',
      smsAlert: false,
      emailAlert: true
    }
  ]

  const emails = [
    { id: 1, from: 'customer@gmail.com', subject: 'Billing Issue', status: 'escalated', aiResponse: 'Pending', sentiment: 'negative', time: '5 min ago' },
    { id: 2, from: 'user@work.com', subject: 'Feature Request', status: 'ai_responded', aiResponse: 'Sent', sentiment: 'positive', time: '22 min ago' },
    { id: 3, from: 'student@training.com', subject: 'Course Access', status: 'pending', aiResponse: 'Reviewing', sentiment: 'neutral', time: '1 hour ago' },
  ]

  const discordQuestions = [
    { id: 1, user: 'StudentA', channel: 'python-help', question: 'How do I debug this error?', aiResponse: 'Sent', confidence: 85, time: '3 min ago' },
    { id: 2, user: 'StudentB', channel: 'advanced-topics', question: 'Complex question about optimization', aiResponse: 'Escalated', confidence: 32, time: '12 min ago' },
    { id: 3, user: 'StudentC', channel: 'exercises', question: 'How do I solve exercise 3?', aiResponse: 'Sent', confidence: 92, time: '25 min ago' },
  ]

  const linkedinMessages = [
    { id: 1, from: 'John Smith (CEO)', type: 'direct_message', message: 'Partnership opportunity', status: 'escalated', time: '18 min ago' },
    { id: 2, from: 'Sarah Lee', type: 'post_comment', message: 'Great article, love the insights!', status: 'responded', time: '45 min ago' },
    { id: 3, from: 'Tech Recruiter', type: 'direct_message', message: 'Interested in opportunities', status: 'pending', time: '2 hours ago' },
  ]

  const getSeverityColor = (severity) => {
    switch(severity) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-300'
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-300'
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300'
      default: return 'bg-blue-100 text-blue-800 border-blue-300'
    }
  }

  const getSeverityIcon = (severity) => {
    switch(severity) {
      case 'critical': return <AlertTriangle className="text-red-600" size={20} />
      case 'high': return <AlertCircle className="text-orange-600" size={20} />
      case 'medium': return <AlertCircle className="text-yellow-600" size={20} />
      default: return <Zap className="text-blue-600" size={20} />
    }
  }

  const getTypeIcon = (type) => {
    switch(type) {
      case 'email': return <Mail size={20} />
      case 'discord': return <MessageSquare size={20} />
      case 'linkedin': return <Linkedin size={20} />
      case 'call': return <Phone size={20} />
      default: return <AlertCircle size={20} />
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Multi-Channel AI Control Center</h1>
        <p className="text-blue-200 mt-1">Monitor all support channels • Real-time escalations • Pager alerts</p>
      </div>

      {/* Critical Alert Banner */}
      {overview.escalations.critical > 0 && (
        <div className="bg-red-500/20 border-l-4 border-red-500 p-4 rounded-r-lg flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="text-red-400" size={24} />
            <div>
              <p className="font-bold text-red-200">🚨 {overview.escalations.critical} Critical Escalation(s) Pending</p>
              <p className="text-sm text-red-300">Immediate human action required</p>
            </div>
          </div>
          <button className="btn-primary bg-red-600 hover:bg-red-700 flex items-center space-x-2">
            <Volume2 size={18} />
            <span>View Urgent</span>
          </button>
        </div>
      )}

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {/* Email */}
        <div className="card hover:shadow-lg transition-shadow">
          <div className="card-body">
            <div className="flex items-center space-x-2 mb-2">
              <Mail className="text-blue-600" size={20} />
              <h3 className="font-semibold text-white">Emails</h3>
            </div>
            <p className="text-3xl font-bold text-white">{overview.email.total}</p>
            <div className="mt-3 space-y-1 text-sm">
              <div className="flex justify-between text-blue-200">
                <span>Apps: {overview.email.apps}</span>
              </div>
              <div className="flex justify-between text-blue-200">
                <span>Training: {overview.email.training}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-orange-600 font-medium">⚠️ {overview.email.pending} pending</span>
              </div>
            </div>
          </div>
        </div>

        {/* Discord */}
        <div className="card hover:shadow-lg transition-shadow">
          <div className="card-body">
            <div className="flex items-center space-x-2 mb-2">
              <MessageSquare className="text-purple-600" size={20} />
              <h3 className="font-semibold text-white">Discord</h3>
            </div>
            <p className="text-3xl font-bold text-white">{overview.discord.total}</p>
            <div className="mt-3 space-y-1 text-sm">
              <div className="flex justify-between text-blue-200">
                <span>Unanswered: {overview.discord.unanswered}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-orange-600 font-medium">⚠️ {overview.discord.escalated} escalated</span>
              </div>
            </div>
          </div>
        </div>

        {/* LinkedIn */}
        <div className="card hover:shadow-lg transition-shadow">
          <div className="card-body">
            <div className="flex items-center space-x-2 mb-2">
              <Linkedin className="text-blue-700" size={20} />
              <h3 className="font-semibold text-white">LinkedIn</h3>
            </div>
            <p className="text-3xl font-bold text-white">{overview.linkedin.total}</p>
            <div className="mt-3 space-y-1 text-sm">
              <div className="flex justify-between text-blue-200">
                <span>DMs: {overview.linkedin.messages}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-orange-600 font-medium">⚠️ {overview.linkedin.escalated} escalated</span>
              </div>
            </div>
          </div>
        </div>

        {/* Calls */}
        <div className="card hover:shadow-lg transition-shadow">
          <div className="card-body">
            <div className="flex items-center space-x-2 mb-2">
              <Phone className="text-green-600" size={20} />
              <h3 className="font-semibold text-white">Calls/SMS</h3>
            </div>
            <p className="text-3xl font-bold text-white">{overview.calls.total}</p>
            <div className="mt-3 space-y-1 text-sm">
              <div className="flex justify-between text-blue-200">
                <span>Today: {overview.calls.today}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-orange-600 font-medium">⚠️ {overview.calls.pending} pending</span>
              </div>
            </div>
          </div>
        </div>

        {/* Escalations */}
        <div className="card border-2 border-red-200 bg-red-50 hover:shadow-lg transition-shadow">
          <div className="card-body">
            <div className="flex items-center space-x-2 mb-2">
              <AlertCircle className="text-red-600" size={20} />
              <h3 className="font-semibold text-white">Escalations</h3>
            </div>
            <p className="text-3xl font-bold text-red-400">{overview.escalations.pending}</p>
            <div className="mt-3 space-y-1 text-sm">
              <div className="flex justify-between text-red-200">
                <span className="font-bold">🚨 {overview.escalations.critical} Critical</span>
              </div>
              <div className="flex justify-between text-red-300">
                <span>High: {overview.escalations.highPriority}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-blue-500/30">
        <div className="flex space-x-8">
          {[
            { id: 'overview', label: 'Escalations Queue', icon: '⚠️' },
            { id: 'email', label: '📧 Email', icon: null },
            { id: 'discord', label: '💬 Discord', icon: null },
            { id: 'linkedin', label: '💼 LinkedIn', icon: null },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-cyber-600 text-cyber-600'
                  : 'border-transparent text-blue-200 hover:text-white'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ESCALATIONS QUEUE - Main View */}
      {activeTab === 'overview' && (
        <div className="space-y-4">
          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold text-white">🚨 Escalation Queue</h3>
            </div>
            <div className="card-body space-y-3">
              {escalations.filter(e => escalationFilter === 'all' || e.severity === escalationFilter).map(alert => (
                <div
                  key={alert.id}
                  className={`border-2 rounded-lg p-4 hover:shadow-md transition-all cursor-pointer ${getSeverityColor(alert.severity)}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start space-x-3">
                      {getSeverityIcon(alert.severity)}
                      <div className="flex-1">
                        <p className="font-bold">{alert.reason}</p>
                        <p className="text-sm opacity-75">{alert.source} • {alert.time}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {alert.smsAlert && <div className="bg-current text-white px-2 py-1 rounded text-xs font-bold">SMS</div>}
                      {alert.emailAlert && <div className="bg-current text-white px-2 py-1 rounded text-xs font-bold">EMAIL</div>}
                    </div>
                  </div>

                  <div className="bg-slate-800/50/30 p-3 rounded mb-3">
                    <p className="text-sm"><strong>From:</strong> {alert.from}</p>
                    <p className="text-sm mt-1">"{alert.preview}"</p>
                  </div>

                  <div className="flex space-x-2">
                    <button className="flex-1 btn-primary text-sm py-2 flex items-center justify-center space-x-1">
                      <Send size={16} />
                      <span>Take Action</span>
                    </button>
                    <button className="flex-1 btn-secondary text-sm py-2">Acknowledge</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* EMAIL SECTION */}
      {activeTab === 'email' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            <div className="card">
              <div className="card-body">
                <p className="text-blue-200 text-sm">Apps Support (iCloud)</p>
                <p className="text-2xl font-bold text-white mt-2">28</p>
                <p className="text-xs text-blue-300/70 mt-1">support@cybertection-guardbot.com</p>
              </div>
            </div>
            <div className="card">
              <div className="card-body">
                <p className="text-blue-200 text-sm">Training Center (Proton)</p>
                <p className="text-2xl font-bold text-white mt-2">19</p>
                <p className="text-xs text-blue-300/70 mt-1">josephplaugher@cybertection-training-center.com</p>
              </div>
            </div>
            <div className="card">
              <div className="card-body">
                <p className="text-blue-200 text-sm">AI Responses Sent</p>
                <p className="text-2xl font-bold text-green-600 mt-2">34</p>
                <p className="text-xs text-blue-300/70 mt-1">Awaiting escalation</p>
              </div>
            </div>
            <div className="card">
              <div className="card-body">
                <p className="text-blue-200 text-sm">Pending Review</p>
                <p className="text-2xl font-bold text-orange-600 mt-2">8</p>
                <p className="text-xs text-blue-300/70 mt-1">Need human response</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold text-white">Recent Emails</h3>
            </div>
            <div className="card-body space-y-2">
              {emails.map(email => (
                <div key={email.id} className="border-b pb-3 last:border-b-0 hover:bg-slate-700/30 p-3 rounded cursor-pointer transition">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <p className="font-medium text-white">{email.subject}</p>
                      <p className="text-sm text-blue-200">{email.from}</p>
                    </div>
                    <span className="text-xs text-blue-300/70">{email.time}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      email.status === 'escalated' ? 'bg-red-500/20 text-red-200' :
                      email.status === 'ai_responded' ? 'bg-green-500/20 text-green-200' :
                      'bg-yellow-500/20 text-yellow-200'
                    }`}>
                      {email.status === 'escalated' ? '⚠️' : email.status === 'ai_responded' ? '✓' : '⏳'} {email.status}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded ${
                      email.sentiment === 'negative' ? 'bg-red-500/20 text-red-200' :
                      email.sentiment === 'positive' ? 'bg-green-500/20 text-green-200' :
                      'bg-slate-700/20 text-blue-200'
                    }`}>
                      {email.sentiment}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* DISCORD SECTION */}
      {activeTab === 'discord' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="card">
              <div className="card-body">
                <p className="text-blue-200 text-sm">Total Questions</p>
                <p className="text-2xl font-bold text-white mt-2">{overview.discord.total}</p>
                <p className="text-xs text-blue-300/70 mt-1">From training students</p>
              </div>
            </div>
            <div className="card">
              <div className="card-body">
                <p className="text-blue-200 text-sm">AI Responses</p>
                <p className="text-2xl font-bold text-green-600 mt-2">{overview.discord.total - overview.discord.unanswered}</p>
                <p className="text-xs text-blue-300/70 mt-1">Successfully answered</p>
              </div>
            </div>
            <div className="card">
              <div className="card-body">
                <p className="text-blue-200 text-sm">Escalated</p>
                <p className="text-2xl font-bold text-orange-600 mt-2">{overview.discord.escalated}</p>
                <p className="text-xs text-blue-300/70 mt-1">Awaiting instructor</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold text-white">Recent Student Questions</h3>
            </div>
            <div className="card-body space-y-2">
              {discordQuestions.map(q => (
                <div key={q.id} className="border-b pb-3 last:border-b-0 hover:bg-slate-700/30 p-3 rounded cursor-pointer transition">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <p className="font-medium text-white">{q.question}</p>
                      <p className="text-sm text-blue-200">#{q.user} in #{q.channel}</p>
                    </div>
                    <span className="text-xs text-blue-300/70">{q.time}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      q.aiResponse === 'Escalated' ? 'bg-orange-100 text-orange-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {q.aiResponse === 'Escalated' ? '⚠️' : '✓'} {q.aiResponse}
                    </span>
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                      {q.confidence}% confident
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* LINKEDIN SECTION */}
      {activeTab === 'linkedin' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="card">
              <div className="card-body">
                <p className="text-blue-200 text-sm">Total Messages</p>
                <p className="text-2xl font-bold text-white mt-2">{overview.linkedin.total}</p>
                <p className="text-xs text-blue-300/70 mt-1">DMs & Comments</p>
              </div>
            </div>
            <div className="card">
              <div className="card-body">
                <p className="text-blue-200 text-sm">Direct Messages</p>
                <p className="text-2xl font-bold text-white mt-2">{overview.linkedin.messages}</p>
                <p className="text-xs text-blue-300/70 mt-1">Professional inquiries</p>
              </div>
            </div>
            <div className="card">
              <div className="card-body">
                <p className="text-blue-200 text-sm">Escalated</p>
                <p className="text-2xl font-bold text-orange-600 mt-2">{overview.linkedin.escalated}</p>
                <p className="text-xs text-blue-300/70 mt-1">Awaiting action</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold text-white">Recent LinkedIn Activity</h3>
            </div>
            <div className="card-body space-y-2">
              {linkedinMessages.map(msg => (
                <div key={msg.id} className="border-b pb-3 last:border-b-0 hover:bg-slate-700/30 p-3 rounded cursor-pointer transition">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <p className="font-medium text-white">{msg.from}</p>
                      <p className="text-sm text-blue-200">{msg.message}</p>
                    </div>
                    <span className="text-xs text-blue-300/70">{msg.time}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      msg.type === 'direct_message' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                    }`}>
                      {msg.type === 'direct_message' ? '💬' : '💭'} {msg.type}
                    </span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      msg.status === 'escalated' ? 'bg-orange-100 text-orange-800' :
                      msg.status === 'responded' ? 'bg-green-100 text-green-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {msg.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
